/**
 * Handing a fetched file to the browser.
 *
 * Every downloadable file this app produces — the xlsx and csv exports, a
 * page image — is served behind the bearer token, so `<a href download>`
 * pointed at the gateway cannot work: the browser's own navigation carries no
 * Authorization header and gets a 401. The bytes are fetched first, wrapped
 * in a blob and handed over from memory, which is what this module does.
 */

/**
 * How the file reached the person, so a caller can say something useful.
 *
 * `newTab` is not a failure: on iOS the file opens in a tab and is saved from
 * the share sheet, which is worth a sentence in the UI ("Die Datei wurde in
 * einem neuen Tab geöffnet"), because nothing lands in a downloads folder and
 * the person would otherwise think the export did nothing.
 */
export type DownloadOutcome = 'download' | 'newTab' | 'blocked'

/**
 * True on iOS and on iPadOS, which reports itself as a Mac and is only
 * distinguishable by having a touch screen.
 *
 * It matters because WebKit on iOS ignores the `download` attribute on a
 * `blob:` URL: the click navigates the current tab to the blob instead of
 * saving it, which throws the person out of the app and loses whatever they
 * had open. Opening a new tab is the one path that both saves the file (via
 * the share sheet) and leaves the app where it was.
 */
function isIosWebKit(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  if (/iPad|iPhone|iPod/.test(ua)) return true
  return /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1
}

/**
 * How long the object URL is kept alive after the click.
 *
 * Revoking immediately is the obvious thing and it is wrong: the browser has
 * only queued the download at that point, and a revoked URL produces a
 * silently empty file. A few seconds covers the hand-off and still frees the
 * blob, which for a large xlsx is real memory.
 */
const REVOKE_AFTER_MS = 10_000

/**
 * Saves a blob under `filename`, or opens it in a new tab where saving is not
 * possible.
 *
 * The caller owns the blob; this function owns the object URL it makes from
 * it and revokes it.
 */
export function saveBlob(blob: Blob, filename: string): DownloadOutcome {
  const url = URL.createObjectURL(blob)
  const revoke = () => URL.revokeObjectURL(url)

  if (isIosWebKit()) {
    // `noopener` would be the usual hardening, but a blob URL is same-origin
    // and the opener reference is what lets a popup blocker's "allow" work
    // here; the risk it guards against does not exist for our own bytes.
    const opened = window.open(url, '_blank')
    window.setTimeout(revoke, REVOKE_AFTER_MS)
    return opened ? 'newTab' : 'blocked'
  }

  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  // Firefox only fires the click when the anchor is in the document.
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(revoke, REVOKE_AFTER_MS)
  return 'download'
}

/**
 * Reads the filename out of a `Content-Disposition` header, so a download is
 * named the way the server named it rather than by a second guess in the
 * client. Returns null when the header is missing or carries no filename.
 *
 * Handles both `filename=` and the RFC 5987 `filename*=UTF-8''…` form; the
 * gateway sends the plain one today (`attachment; filename=export.xlsx`), but
 * a German filename would have to arrive encoded.
 */
export function filenameFromDisposition(disposition?: string | null): string | null {
  if (!disposition) return null

  const extended = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(disposition)
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      // A malformed encoding is not worth failing a download over: fall
      // through to the plain parameter, then to the caller's default.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(disposition)
  if (plain) {
    const value = (plain[2] ?? plain[1] ?? '').trim()
    if (value) return value
  }
  return null
}

/**
 * Reads a blob as text.
 *
 * `Blob.text()` is the obvious way and cannot be relied on: jsdom, which the
 * tests run in, and Safari before 14 have no such method, and the failure is
 * a TypeError in the middle of error handling — exactly where a second
 * failure is least welcome. FileReader exists everywhere.
 */
export function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(blob)
  })
}
